import React from 'react';
import { uniqueId } from 'lodash';
import { useTranslation } from 'react-i18next';
import { FastField, Field, FieldProps, useFormikContext } from 'formik';
import { Checkbox, Select } from '@brighterion/br-ui';
import { FormikFieldString } from 'Components/FormikComponents';
import type { RuleGroupConditionsSchema } from 'Components/RuleGroupConditionsForm/ruleGroupConditionsSchema';

import styles from './Conditions.css';

type ConditionsProps = {
    index: number;
};

const Conditions = ({ index }: ConditionsProps) => {
    const { t } = useTranslation();
    const { values, isSubmitting, handleBlur, handleChange } = useFormikContext<RuleGroupConditionsSchema>();

    /** Demo purpose only */
    const list = [
        {
            label: 'Label 1',
            value: '1'
        },
        {
            label: 'Label 2',
            value: '2'
        }
    ];

    return (
        <div className={styles.conditionContainer}>
            <div className={styles.conditionColumn}>
                <span className={styles.conditionLabel}>{index === 0 ? t('WHEN') : t('AND')}</span>
            </div>

            <div className={styles.conditionColumn}>
                {/** Target field parameter within Condition  */}
                <FastField name={`conditions.${index}.parameter`}>
                    {({ field, form: { setFieldValue } }: FieldProps) => {
                        return (
                            <Select
                                size='sm'
                                placeholder={`- ${t('ruleGroupConditions.form.parameter.placeholder')} -`}
                                handleClickCallback={(value) => {
                                    /**
                                     * Remove value from Action
                                     * @note Please, don't get confused by Formik field.value.
                                     */
                                    setFieldValue(`conditions.${index}.operator`, '');
                                    setFieldValue(`conditions.${index}.values`, '');
                                    setFieldValue(field.name, value.toString());
                                }}
                                selectedItems={field.value ? [field.value] : []}
                                disabled={isSubmitting}
                                aria-disabled={isSubmitting}
                            >
                                {list.map(({ value, label }) => {
                                    return (
                                        <option key={uniqueId(value)} value={value}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </Select>
                        );
                    }}
                </FastField>
            </div>

            <div className={styles.conditionColumn}>
                {values.conditions[index].parameter && (
                    <FastField name={`conditions.${index}.operator`}>
                        {({ field, form: { setFieldValue } }: FieldProps) => {
                            return (
                                <Select
                                    size='sm'
                                    placeholder={`- ${t('ruleGroupConditions.form.operator.placeholder')} -`}
                                    handleClickCallback={(value) => {
                                        /**
                                         * Remove values from Action
                                         * @note Please, don't get confused by Formik field.value.
                                         */
                                        setFieldValue(`conditions.${index}.values`, '');
                                        setFieldValue(field.name, value.toString());
                                    }}
                                    selectedItems={field.value ? [field.value] : []}
                                    disabled={isSubmitting}
                                    aria-disabled={isSubmitting}
                                >
                                    {list.map(({ value, label }) => {
                                        return (
                                            <option key={uniqueId(value)} value={value}>
                                                {label}
                                            </option>
                                        );
                                    })}
                                </Select>
                            );
                        }}
                    </FastField>
                )}
            </div>

            <div className={styles.conditionColumn}>
                {values.conditions[index].operator && (
                    <Field name={`conditions.${index}.values`}>
                        {({ field, meta }: FieldProps) => {
                            return (
                                <FormikFieldString
                                    className={styles.formikFieldString}
                                    name={field.name}
                                    size='sm'
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    type='text'
                                    disabled={isSubmitting}
                                    error={!!(meta.error && meta.touched)}
                                    errorMsg={meta.error?.toString()}
                                />
                            );
                        }}
                    </Field>
                )}
            </div>

            {/** This empty div creates space within the grid */}
            <div className={styles.conditionColumn} />

            <div className={styles.conditionColumn}>
                <Field name={`conditions.${index}.readonly`}>
                    {({ field, form: { setFieldValue } }: FieldProps) => {
                        return (
                            <Checkbox
                                name={field.name}
                                checked={field.value}
                                onChange={(event) => setFieldValue(field.name, event.currentTarget.checked)}
                                disabled={isSubmitting}
                            />
                        );
                    }}
                </Field>
            </div>
        </div>
    );
};

export default Conditions;
